/* Machine × day efficiency grid.
 *
 * One row per machine, one cell per day, on a ramp that diverges at the
 * benchmark: red below it, the ground colour at it, blue above. Unlike the
 * Production page's heatmap the ramp is fixed (55–105%) rather than scaled
 * to the month, because the rows are different machines and have to be
 * comparable with each other, not just with themselves.
 *
 * The colours are computed here, so the component has to be told whether
 * the page is dark — pass `useIsDark()`, not the stored preference.
 */

type Rgb = [number, number, number]

const LIGHT = { lo: [168, 50, 63] as Rgb, mid: [238, 241, 244] as Rgb, hi: [47, 95, 143] as Rgb }
const DARK = { lo: [255, 93, 108] as Rgb, mid: [28, 39, 67] as Rgb, hi: [79, 140, 255] as Rgb }

export const HEAT_LO = 55
export const HEAT_HI = 105
const LEGEND = [55, 62, 69, 76, 83, 90, 96, 101, 105]

const mix = (a: Rgb, b: Rgb, t: number): Rgb =>
  [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * t)) as Rgb

function heat(eff: number, bench: number, dark: boolean): Rgb {
  const ramp = dark ? DARK : LIGHT
  const e = Math.max(HEAT_LO, Math.min(HEAT_HI, eff))
  return e < bench
    ? mix(ramp.lo, ramp.mid, (e - HEAT_LO) / Math.max(1, bench - HEAT_LO))
    : mix(ramp.mid, ramp.hi, (e - bench) / Math.max(1, HEAT_HI - bench))
}

const css = (c: Rgb) => `rgb(${c.join(',')})`
/** Label colour from the cell's own brightness — the ramp crosses both. */
const ink = ([r, g, b]: Rgb) => (0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#1d2d3d' : '#f4f6fb')

export function HeatLegend({ bench, dark }: { bench: number; dark: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, fontSize: 11,
      color: 'var(--color-neutral-600)',
    }}>
      <span>{HEAT_LO}%</span>
      {LEGEND.map((v) => (
        <span key={v} style={{
          width: 22, height: 12, display: 'block', background: css(heat(v, bench, dark)),
        }} />
      ))}
      <span>{HEAT_HI}%</span>
    </div>
  )
}

/** `eff` is null on a day the machine has nothing rated. `edge` is a
 *  box-shadow drawn inside the cell — the dark line on the left edge that
 *  marks the day a machine changed count. */
export interface HeatCell { eff: number | null; title: string; edge?: string }

export function MachineHeat({ days, rows, bench, dark, onOpen }: {
  /** Column heads — day of month. */
  days: string[]
  /** `cells` is one entry per day, in the order of `days`. */
  rows: { key: string; label: string; cells: HeatCell[] }[]
  bench: number
  dark: boolean
  onOpen: (key: string) => void
}) {
  const cols = `150px repeat(${days.length}, minmax(20px,1fr))`
  return (
    <div className="scroll-x" style={{ marginTop: 16 }}>
      <div style={{ minWidth: 900 }}>
        <div style={{
          display: 'grid', gridTemplateColumns: cols, gap: 2, fontSize: 10,
          color: 'var(--color-neutral-600)', marginBottom: 4,
        }}>
          <div />
          {days.map((d, i) => <div key={i} style={{ textAlign: 'center' }}>{d}</div>)}
        </div>
        {rows.map((row) => (
          <div key={row.key} style={{
            display: 'grid', gridTemplateColumns: cols, gap: 2, marginBottom: 2,
          }}>
            <button type="button" className="truncate" title={row.label} onClick={() => onOpen(row.key)} style={{
              font: 'inherit', fontSize: 11, textAlign: 'left', background: 'none', border: 0,
              padding: '0 6px 0 0', color: 'var(--color-neutral-800)', cursor: 'pointer',
            }}>{row.label}</button>
            {row.cells.map((c, i) => {
              const bg = c.eff !== null ? heat(c.eff, bench, dark) : null
              return (
                <div key={i} className="num" title={c.title}
                  style={{
                    height: 19, fontSize: 9, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', boxShadow: c.edge,
                    background: bg ? css(bg) : 'var(--c-track)',
                    color: bg ? ink(bg) : 'var(--color-neutral-600)',
                  }}>{c.eff !== null ? c.eff.toFixed(0) : ''}</div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
